import PropTypes from 'prop-types';
import { BsCheckLg, BsXLg } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import Panel from '#globalComponents/Overlay/Panel';
import { formatDate } from '#lib/dateUtils';

import CancelAnywayButton from '../components/CancelAnywayButton';
import OptionCard from '../components/OptionCard';

const PAUSE_EFFECTS = [
	{ allowed: true, text: 'Leads und Kampagnen bleiben einsehbar' },
	{ allowed: false, text: 'Keine neue Lead-Recherche' },
	{ allowed: false, text: 'Kein E-Mail- und Briefversand' },
	{ allowed: false, text: 'Kein CSV-Export' },
	{ allowed: false, text: 'CRM-Add-on pausiert' },
	{ allowed: false, text: 'Keine Credits – Ihr Guthaben wird zurückgesetzt und beim Fortsetzen neu vergeben' },
];

const monthsLabel = (months) => `${months} ${months === 1 ? 'Monat' : 'Monate'}`;

export default function PauseStep({
	pauseOptions,
	months,
	onMonthsChange,
	onConfirm,
	onCancelAnyway,
	cancelAnywayLabel,
	wasCancelling = false,
	onBack,
	isLoading = false,
	onClose,
}) {
	const selectedOption = pauseOptions.find((option) => option.months === months) ?? null;
	const startsAt = pauseOptions[0]?.startsAt ?? null;
	const startText = startsAt ? `am ${formatDate(startsAt)}` : 'zum Ende Ihres bezahlten Zeitraums';

	return (
		<>
			<Panel.Header onClose={onClose}>Abonnement pausieren statt kündigen</Panel.Header>

			<Panel.Body>
				<div className="space-y-5">
					<p className="text-sm text-text">
						Legen Sie eine Pause ein – ohne Kosten, ohne Datenverlust. Die Pause beginnt {startText} und Ihr
						Abo läuft danach automatisch weiter.
						{wasCancelling && ' Ihre Kündigung wird damit zurückgenommen.'}
					</p>

					{pauseOptions.length === 0 ? (
						<Alert tone="caution">Eine Pause ist für Ihr Abonnement derzeit nicht möglich.</Alert>
					) : (
						<fieldset>
							<legend className="block text-sm font-medium text-text mb-2">Dauer der Pause</legend>
							<div className="space-y-2">
								{pauseOptions.map((option) => (
									<OptionCard
										key={option.months}
										name="pauseMonths"
										value={String(option.months)}
										checked={months === option.months}
										onChange={(value) => onMonthsChange(Number(value))}
										title={monthsLabel(option.months)}
										description={`Abo läuft am ${formatDate(option.resumesAt)} weiter`}
										disabled={isLoading}
									/>
								))}
							</div>
						</fieldset>
					)}

					<div className="rounded-card border border-bgBase-500 bg-bgBase-200 p-4">
						<p className="mb-2 text-sm font-semibold text-text">Während der Pause</p>
						<ul className="space-y-1.5">
							{PAUSE_EFFECTS.map((effect) => (
								<li key={effect.text} className="flex items-start gap-2 text-sm text-text">
									{effect.allowed ? (
										<BsCheckLg className="mt-0.5 h-4 w-4 shrink-0 text-success-700" />
									) : (
										<BsXLg className="mt-0.5 h-4 w-4 shrink-0 text-bgBase-800" />
									)}
									<span>{effect.text}</span>
								</li>
							))}
						</ul>
						<p className="mt-3 text-xs text-bgBase-800">
							Sie können die Pause jederzeit vorzeitig beenden.
						</p>
					</div>
				</div>
			</Panel.Body>

			<Panel.Footer stacked>
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
					<Button
						variant="solid"
						size="md"
						fullWidth
						loading={isLoading}
						disabled={isLoading || !selectedOption}
						onClick={onConfirm}
					>
						{`Für ${monthsLabel(months)} pausieren`}
					</Button>
					<CancelAnywayButton onClick={onCancelAnyway} disabled={isLoading} label={cancelAnywayLabel} />
				</div>

				{onBack && (
					<div>
						<Button variant="outline" size="sm" onClick={onBack} disabled={isLoading}>
							Zurück
						</Button>
					</div>
				)}
			</Panel.Footer>
		</>
	);
}

PauseStep.propTypes = {
	pauseOptions: PropTypes.arrayOf(
		PropTypes.shape({
			months: PropTypes.number.isRequired,
			startsAt: PropTypes.string.isRequired,
			resumesAt: PropTypes.string.isRequired,
		}),
	).isRequired,
	months: PropTypes.number.isRequired,
	onMonthsChange: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	onCancelAnyway: PropTypes.func.isRequired,
	cancelAnywayLabel: PropTypes.string,
	wasCancelling: PropTypes.bool,
	onBack: PropTypes.func,
	isLoading: PropTypes.bool,
	onClose: PropTypes.func,
};
